import { useState, useEffect, useCallback } from 'react';
import { releaseAPI, uploadFile, deleteAttachment } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Trash2, Sparkles, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { AttachmentGallery } from './AttachmentGallery';
import { PendingFilesList, validateFiles } from './FileFields';

const today = () => new Date().toISOString().slice(0, 10);

function SummaryCard({ summary, isAdmin, currentUser, onChanged }) {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const canAttach = isAdmin || summary.author_id === currentUser?.id;

  const handleUpload = async () => {
    setBusy(true);
    try {
      for (const f of pending) {
        await uploadFile(f, null, null, null, null, null, summary.id);
      }
      setPending([]);
      toast.success('Screenshots added');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this summary?')) return;
    try {
      await releaseAPI.delete(summary.id);
      toast.success('Summary deleted');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleDeleteShot = async (id) => {
    try {
      await deleteAttachment(id);
      onChanged();
    } catch (err) {
      toast.error('Failed to delete screenshot');
    }
  };

  return (
    <div className="border border-[#E0E0E0] rounded-lg p-3 bg-[#FAFBFD]" data-testid={`release-summary-${summary.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] text-[#666666]">
          <span className="px-1.5 py-0.5 rounded bg-[#CFFF40] text-[#101010] font-semibold">
            {new Date(summary.summary_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
          </span>
          <span>{summary.author_name}</span>
        </div>
        {isAdmin && (
          <button onClick={handleDelete} className="text-[#999999] hover:text-[#DC2626]" data-testid={`delete-summary-${summary.id}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <p className="text-sm text-[#111111] mt-2 whitespace-pre-wrap">{summary.content}</p>
      <AttachmentGallery attachments={summary.screenshots || []} canDelete={canAttach} onDelete={handleDeleteShot} />
      {canAttach && (
        <div className="mt-2">
          <PendingFilesList pendingFiles={pending} onRemovePending={(idx) => setPending(p => p.filter((_, i) => i !== idx))} />
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => { validateFiles(e.target.files, (files) => setPending(p => [...p, ...files])); e.target.value = ''; }}
              className="text-xs h-8"
              data-testid={`summary-shot-input-${summary.id}`}
            />
            {pending.length > 0 && (
              <Button size="sm" onClick={handleUpload} disabled={busy} data-testid={`summary-shot-upload-${summary.id}`}>
                <ImageIcon size={13} className="mr-1" /> {busy ? '...' : 'Upload'}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ReleaseSummarySection({ taskId, canAdd, isAdmin, currentUser }) {
  const [summaries, setSummaries] = useState([]);
  const [adding, setAdding] = useState(false);
  const [content, setContent] = useState('');
  const [summaryDate, setSummaryDate] = useState(today());
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const res = await releaseAPI.listByTask(taskId);
      setSummaries(res.data);
    } catch (err) {
      // section stays empty if not permitted
    }
  }, [taskId]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleAdd = async () => {
    if (!content.trim()) {
      toast.error('Summary text is required');
      return;
    }
    setSaving(true);
    try {
      const res = await releaseAPI.create({ dev_task_id: taskId, content, summary_date: summaryDate });
      for (const f of pending) {
        await uploadFile(f, null, null, null, null, null, res.data.id);
      }
      setContent('');
      setPending([]);
      setSummaryDate(today());
      setAdding(false);
      toast.success('Summary added');
      fetch();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add summary');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="release-summary-section">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5">
          <Sparkles size={14} className="text-[#8B5CF6]" /> Release summaries
          {summaries.length > 0 && <span className="text-[#999999] font-normal">({summaries.length})</span>}
        </Label>
        {canAdd && !adding && (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)} data-testid="add-summary-btn">
            <Plus size={14} className="mr-1" /> Add summary
          </Button>
        )}
      </div>

      <div className="space-y-2 mt-2">
        {summaries.length === 0 && !adding && (
          <p className="text-xs text-[#999999]">No summaries yet. Add one when a piece of work is shipped.</p>
        )}
        {summaries.map(s => (
          <SummaryCard key={s.id} summary={s} isAdmin={isAdmin} currentUser={currentUser} onChanged={fetch} />
        ))}
      </div>

      {adding && (
        <div className="border border-[#CFFF40] rounded-lg p-3 mt-2 bg-white" data-testid="new-summary-form">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            placeholder="What was shipped? One line per point."
            data-testid="new-summary-content"
          />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={summaryDate} onChange={(e) => setSummaryDate(e.target.value)} data-testid="new-summary-date" />
            </div>
            <div>
              <Label className="text-xs">Screenshots <span className="text-[#999999] font-normal">(optional)</span></Label>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => { validateFiles(e.target.files, (files) => setPending(p => [...p, ...files])); e.target.value = ''; }}
                data-testid="new-summary-files"
              />
            </div>
          </div>
          <PendingFilesList pendingFiles={pending} onRemovePending={(idx) => setPending(p => p.filter((_, i) => i !== idx))} />
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => { setAdding(false); setContent(''); setPending([]); }} data-testid="cancel-summary-btn">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAdd} disabled={saving} className="bg-[#101010] hover:bg-[#2A2A2A] text-[#CFFF40]" data-testid="save-summary-btn">
              {saving ? 'Saving...' : 'Save summary'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
